import { useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@design-system/components/button/button';
import { FaqSuggestionsModal } from '../primitives/FaqSuggestionsModal';
import { RepeatingFieldShell } from '../primitives/RepeatingFieldShell';
import { FaqItem } from './FaqItem';
import { useSectionOpenState } from '../../lib/useSectionOpenState';
import { emptyFaq, ProgramFormValues } from '../../model/programForm.types';

export type FaqsSectionProps = {
  isSubmitting?: boolean;
};

export const FaqsSection = ({ isSubmitting }: FaqsSectionProps) => {
  const {
    control,
    clearErrors,
    formState: { errors, submitCount },
  } = useFormContext<ProgramFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'faqs',
  });
  const errorAnchorRef = useRef<HTMLDivElement>(null);
  const faqsRootError = errors.faqs?.root?.message ?? errors.faqs?.message;
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const { isOpen, setIsOpen } = useSectionOpenState({
    submitCount,
    hasError: !!errors.faqs,
    errorAnchorRef,
  });

  return (
    <Controller
      control={control}
      name="faqsEnabled"
      render={({ field }) => (
        <>
          <RepeatingFieldShell
            title="FAQ"
            subtitle="Frequently Asked Questions (FAQ) are a compilation of common questions and answers related to this program."
            itemLabel="FAQ"
            fields={fields}
            enabled={field.value}
            onToggleEnabled={(next) => {
              field.onChange(next);
              if (!next) clearErrors('faqs');
            }}
            onRemove={remove}
            renderItem={(index) => (
              <FaqItem index={index} isSubmitting={isSubmitting} />
            )}
            isSubmitting={isSubmitting}
            testId="program-form-faqs"
            errorMessage={faqsRootError}
            errorAnchorRef={errorAnchorRef}
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            footer={
              <>
                <Button
                  color="primaryTonal"
                  leftIcon={faPlus}
                  onClick={() => append(emptyFaq())}
                  disabled={isSubmitting}
                  testId="program-form-faqs-add"
                >
                  Add
                </Button>
                <Button
                  color="neutral"
                  variant="text"
                  onClick={() => setIsSuggestionsOpen(true)}
                  disabled={isSubmitting}
                  testId="program-form-faqs-suggestions"
                >
                  Show FAQ suggestions
                </Button>
              </>
            }
          />
          <FaqSuggestionsModal
            isOpen={isSuggestionsOpen}
            onClose={() => setIsSuggestionsOpen(false)}
            onSelect={(suggestion) => append(suggestion)}
          />
        </>
      )}
    />
  );
};
